import { useState, useEffect } from "react";
import semver from "semver";
import { UAParser } from "ua-parser-js";

import type { SemVer } from "semver";

/**
 * Types for the experimental User-Agent Client Hints feature.
 *
 * Client Hints that aim to provide developers with the ability to perform
 * agent-based content negotiation when necessary, while avoiding the
 * historical baggage and passive fingerprinting surface exposed by
 * the venerable User-Agent header.
 *
 * Read more: https://wicg.github.io/ua-client-hints/
 * Interface: https://wicg.github.io/ua-client-hints/#interface
 */

interface NavigatorUABrandVersion {
	readonly brand: string;
	readonly version: string;
}

interface UADataValues {
	readonly brands?: NavigatorUABrandVersion[];
	readonly mobile?: boolean;
	readonly platform?: string;
	readonly architecture?: string;
	readonly bitness?: string;
	readonly formFactor?: string[];
	readonly model?: string;
	readonly platformVersion?: string;
	/** @deprecated in favour of fullVersionList */
	readonly uaFullVersion?: string;
	readonly fullVersionList?: NavigatorUABrandVersion[];
	readonly wow64?: boolean;
}

interface UALowEntropyJSON {
	readonly brands: NavigatorUABrandVersion[];
	readonly mobile: boolean;
	readonly platform: string;
}

interface NavigatorUAData extends UALowEntropyJSON {
	getHighEntropyValues: (hints: string[]) => Promise<UADataValues>;
	toJSON: () => UALowEntropyJSON;
}

declare interface NavigatorUA {
	readonly userAgentData?: NavigatorUAData;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
declare interface Navigator extends NavigatorUA {}

// eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-object-type
declare interface WorkerNavigator extends NavigatorUA {}

type DeviceType = "desktop" | "tablet" | "mobile";

export type DeviceProfile = {
	browser: SupportedBrowser | string | undefined;
	deviceType: DeviceType | undefined;
	os: OS | undefined;
	osVersion: OSVersion | undefined;
};

type HintedDevice = {
	browser: string | undefined;
	isMobile: boolean;
	osName: OS | undefined;
	osVersion: OSVersion | undefined;
};

type OS = "android" | "macOS" | "ios" | "windows";

type OSVersion = Pick<SemVer, "major" | "minor" | "patch">;

type ParsedDeviceUA = {
	browserName: string | undefined;
	deviceType: DeviceType;
	osName: OS | undefined;
	osVersion: OSVersion | undefined;
	vendor: string | undefined;
};

type SupportedBrowser = "brave" | "mobile-safari" | "safari" | string;

/**
 * Retrieves the browser brand name from the last entry of the given
 * NavigatorUABrandVersion array.
 *
 * @param {NavigatorUABrandVersion[] | undefined} brands - An array of
 *        NavigatorUABrandVersion objects, or undefined. Each object
 *        contains details about a browser brand and its version.
 * @returns {string | undefined} The brand name of the last entry in the
 *          array, or undefined if the array is undefined or empty.
 */
const browserFromEntry = (brands: NavigatorUABrandVersion[] | undefined): SupportedBrowser | undefined => {
	if (brands === undefined || brands.length === 0) {
		return undefined;
	}

	const { brand } = brands[brands.length - 1] ?? {};

	if (brand) {
		return brand.toLowerCase();
	}

	return undefined;
};

/**
 * Asynchronously retrieves detailed information about the device, including
 * whether it is a mobile device, the browser brand, and the operating system
 * name and version. This helper is to be used as an internal dependency for
 * the mobile download funnel. If there's the need to be used elsewhere, migrate
 * this to the shared/utils module.
 *
 * @returns {Promise<HintedDevice | undefined>} A promise that resolves to an
 *          object containing device information or undefined if the
 *          information cannot be retrieved.
 */
const hintedDevice = async (): Promise<HintedDevice | undefined> => {
	const uaData = (window.navigator as Navigator).userAgentData;

	if (!uaData) {
		return;
	}

	try {
		const entropy = await uaData.getHighEntropyValues(["brands", "device", "platform", "platformVersion"]);

		const isMobile = entropy.mobile === true;
		const browser = browserFromEntry(entropy.brands);
		const osName = entropy.platform?.toLowerCase() ?? undefined;
		let osVersion = undefined;

		if (semver.valid(entropy.platformVersion)) {
			osVersion = semver.parse(entropy.platformVersion) ?? undefined;

			if (osVersion) {
				osVersion = {
					major: osVersion.major,
					minor: osVersion.minor,
					patch: osVersion.patch,
				};
			}
		}

		let normalizedOsName: OS | undefined = undefined;

		if (osName === "macos") {
			normalizedOsName = "macOS";
		} else if (osName === "android" || osName === "ios" || osName === "windows") {
			normalizedOsName = osName;
		}

		return { isMobile, browser, osName: normalizedOsName, osVersion };
	} catch (e) {
		console.error(e);
		return undefined;
	}
};

const parsedDevice = (userAgent: string): ParsedDeviceUA => {
	const uaParser = new UAParser(userAgent);
	const parsedDevice = uaParser.getDevice();
	const parsedOS = uaParser.getOS();
	const parsedBrowser = uaParser.getBrowser();

	const deviceType = parsedDevice.type?.toLocaleLowerCase();
	const osName = parsedOS.name?.toLowerCase();
	const vendor = parsedDevice.vendor?.toLowerCase();

	let normalizedOSVersion = undefined;

	if (parsedOS.version && semver.valid(parsedOS.version)) {
		if (parsedDevice.model?.toLowerCase() === "k" && semver.major(parsedOS.version) >= 10) {
			normalizedOSVersion = semver.coerce("13") ?? undefined;
		} else {
			normalizedOSVersion = semver.coerce(parsedOS.version) ?? undefined;
		}

		if (normalizedOSVersion !== undefined) {
			normalizedOSVersion = {
				major: normalizedOSVersion.major,
				minor: normalizedOSVersion.minor,
				patch: normalizedOSVersion.patch,
			};
		}
	}

	let normalizedOsName: OS | undefined = undefined;

	if (osName === "mac os") {
		normalizedOsName = "macOS";
	} else if (osName === "android" || osName === "ios" || osName === "windows") {
		normalizedOsName = osName;
	}

	let normalizedDeviceType: DeviceType = "desktop";

	if (deviceType === "mobile" || deviceType === "tablet") {
		normalizedDeviceType = deviceType;
	}

	let normalizedBrowserName: SupportedBrowser | undefined = undefined;

	if (parsedBrowser.name?.toLocaleLowerCase() === "mobile safari") {
		normalizedBrowserName = "mobile-safari";
	} else if (parsedBrowser.name?.toLocaleLowerCase() === "safari") {
		normalizedBrowserName = "safari";
	} else if (parsedBrowser?.name?.toLocaleLowerCase() === "brave") {
		normalizedBrowserName = "brave";
	}

	return {
		browserName: normalizedBrowserName,
		deviceType: normalizedDeviceType,
		osName: normalizedOsName,
		osVersion: normalizedOSVersion,
		vendor,
	};
};

export const detectDevice = async (): Promise<DeviceProfile | void> => {
	if (typeof window === "undefined") {
		return;
	}

	if (window.navigator === undefined) {
		return;
	}

	const uaHints = await hintedDevice();
	const parsedUA = parsedDevice(window.navigator.userAgent);

	let os: OS | undefined = parsedUA.osName;

	if (uaHints?.osName) {
		os = uaHints.osName;
	}

	const osVersion = uaHints?.osVersion || parsedUA.osVersion;

	let deviceType = parsedUA.deviceType;

	if (uaHints && uaHints.isMobile) {
		deviceType = "mobile";
	}

	let browser = parsedUA.browserName;

	if (uaHints && uaHints.browser) {
		browser = uaHints.browser;
	}

	return {
		os,
		osVersion,
		deviceType,
		browser,
	};
};

type DetectionStatus = "pending" | "finished";

export const useClientSideDeviceDetection = (): {
	deviceProfile: DeviceProfile;
	detectionStatus: DetectionStatus;
} => {
	const [detectionStatus, setDetectionStatus] = useState<DetectionStatus>("pending");
	const [deviceProfile, setDeviceProfile] = useState<DeviceProfile>({
		browser: undefined,
		deviceType: undefined,
		os: undefined,
		osVersion: undefined,
	});

	useEffect(() => {
		const detect = async (): Promise<void> => {
			const deviceProfile = await detectDevice();

			if (deviceProfile) {
				setDeviceProfile(deviceProfile);
			}

			setDetectionStatus("finished");
		};
		detect();
	}, []);

	return { deviceProfile, detectionStatus };
};

export const isSupportedIosVersion = (os: DeviceProfile["os"], osVersion: DeviceProfile["osVersion"]): boolean => {
	if (os !== "ios" || !osVersion) {
		return false;
	}

	return (osVersion.major >= 17 && osVersion.minor >= 4) || osVersion.major >= 18;
};

export const isBrowserSupportedIos = (browser: string | undefined): boolean =>
	browser === "safari" || browser === "mobile-safari" || browser === "brave";
